<!--
  The time zone row, under the next-session card: the device is in a
  different zone from the profile, and the member has availability, so the
  change is a choice (see useTimezoneSync). The row opens a small window that
  says what the change does, with the two answers as buttons. It used to be
  a yellow bar above every page.

  In the icon rail the row is a clock with a dot.
-->
<template>
  <VDropdown
    v-if="ask"
    :placement="placement"
    :distance="8"
    class="gm-sb-tz"
    @apply-show="isOpen = true"
    @apply-hide="onHide"
  >
    <button
      ref="row"
      v-tooltip="{
        content: `Times show in ${fromPlace} time`,
        placement: 'right',
        disabled: !isRail || isOpen,
      }"
      type="button"
      class="gm-sb-tz-row"
      aria-haspopup="dialog"
      :aria-expanded="isOpen"
    >
      <span class="gm-sb-tz-icon">
        <ClockIcon aria-hidden="true" />
        <span class="gm-sb-tz-dot" aria-hidden="true" />
      </span>
      <span class="gm-sb-tz-body">
        <span class="gm-sb-tz-line">Times show in {{ fromPlace }} time</span>
        <span class="gm-sb-tz-sub">
          You are in {{ toPlace }}.
          <span class="gm-sb-tz-go">Update</span>
        </span>
      </span>
    </button>
    <template #popper="{ hide }">
      <div class="gm-sb-tz-pop" role="dialog" :aria-labelledby="titleId">
        <p :id="titleId" class="gm-sb-tz-pop-title">
          Change your time zone to {{ toPlace }}?
        </p>
        <p>
          Your available hours keep their clock times. For example, 9:00 to
          17:00 in {{ fromPlace }} becomes 9:00 to 17:00 in {{ toPlace }}.
        </p>
        <p>Sessions you have booked do not move.</p>
        <div class="gm-sb-tz-pop-actions">
          <BaseButton
            size="small"
            color="primary"
            is-block
            @click="(change(), hide())"
          >
            Change to {{ toPlace }} time
          </BaseButton>
          <BaseButton
            size="small"
            color="outline-gray"
            is-block
            @click="(keep(), hide())"
          >
            Keep {{ fromPlace }} time
          </BaseButton>
        </div>
      </div>
    </template>
  </VDropdown>
</template>

<script setup lang="ts">
import { computed, ref, useId, useTemplateRef } from 'vue'
import ClockIcon from '~icons/lucide/clock'
import BaseButton from '@/shared/components/BaseButton.vue'
import {
  useTimezoneSync,
  zonePlace,
} from '@/shared/composables/useTimezoneSync'

// The window opens over the page, beside the row. The phone sheet has no
// room at the side, so there it opens above the row.
withDefaults(
  defineProps<{
    isRail: boolean
    placement?: 'right-start' | 'top-start'
  }>(),
  { placement: 'right-start' },
)

const { ask, change, keep } = useTimezoneSync()

const fromPlace = computed(() => (ask.value ? zonePlace(ask.value.from) : ''))
const toPlace = computed(() => (ask.value ? zonePlace(ask.value.to) : ''))

const titleId = useId()
const row = useTemplateRef<HTMLButtonElement>('row')
const isOpen = ref(false)

// Escape and the two buttons close the window while the focus is in it. The
// focus then goes back to the row. After a click outside, it stays where the
// member put it.
const onHide = () => {
  isOpen.value = false
  if (document.activeElement?.closest('.v-popper__popper')) row.value?.focus()
}
</script>

<style>
/* Unscoped, like the rest of the sidebar (see AppSidebar): the icon rail rule
   keys off the sidebar's own width. Every class starts with gm-sb. */
.gm-sb-tz {
  display: block;
  margin-top: 0.5rem;
}
/* --gm-sb-tz-bg: the fill of the row. The ring round the dot takes it too,
   so the ring never shows on hover. */
.gm-sb-tz-row {
  --gm-sb-tz-bg: var(--color-neutral-50);
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.625rem;
  border-radius: 0.875rem;
  background: var(--gm-sb-tz-bg);
  box-shadow: inset 0 0 0 1px var(--color-neutral-100);
  text-align: left;
  transition: background-color 120ms ease;
}
.gm-sb-tz-row:hover,
.gm-sb-tz.v-popper--shown .gm-sb-tz-row {
  --gm-sb-tz-bg: var(--color-neutral-100);
}
.gm-sb-tz-row:focus-visible {
  outline: 2px solid var(--color-primary-500);
  outline-offset: 2px;
}
.gm-sb-tz-icon {
  position: relative;
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  flex: none;
  color: var(--color-neutral-500);
}
.gm-sb-tz-icon svg {
  width: 1.25rem;
  height: 1.25rem;
}
/* The same dot as the one on the profile photo, on the rim of the clock. */
.gm-sb-tz-dot {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: var(--color-warning-800);
  box-shadow: 0 0 0 2px var(--gm-sb-tz-bg);
  transition: box-shadow 120ms ease;
}
.gm-sb-tz-body {
  min-width: 0;
  display: grid;
  line-height: 1.3;
}
.gm-sb-tz-line {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-neutral-900);
}
.gm-sb-tz-sub {
  font-size: 0.8125rem;
  color: var(--color-neutral-500);
}
.gm-sb-tz-go {
  font-weight: 600;
  color: var(--color-secondary-700);
}

/* The window renders in the page body, outside the sidebar. */
.gm-sb-tz-pop {
  width: 19rem;
  max-width: calc(100vw - 2rem);
  padding: 1rem;
  display: grid;
  gap: 0.5rem;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--color-neutral-600);
}
.gm-sb-tz-pop-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-neutral-900);
}
.gm-sb-tz-pop-actions {
  display: grid;
  gap: 0.5rem;
  margin-top: 0.375rem;
}

/* A phone shows the window in the More sheet, above the row and as wide as
   the row. */
@media (max-width: 47.99rem) {
  .gm-sb-tz-pop {
    width: calc(100vw - 2.5rem - 2px);
    max-width: none;
  }
}

@container gm-sb (max-width: 7.5rem) {
  .gm-sb-tz {
    margin-top: 0.375rem;
  }
  .gm-sb-tz-row {
    --gm-sb-tz-bg: #fff;
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;
    justify-content: center;
    border-radius: 0.625rem;
    box-shadow: none;
  }
  .gm-sb-tz-row:hover,
  .gm-sb-tz.v-popper--shown .gm-sb-tz-row {
    --gm-sb-tz-bg: var(--color-neutral-50);
  }
  /* The clock is one of the rail's icons, and its dot sits where the dots
     of the other icons sit. */
  .gm-sb-tz-icon {
    color: var(--color-neutral-600);
  }
  .gm-sb-tz-dot {
    top: 0.25rem;
    right: 0.25rem;
  }
  .gm-sb-tz-row:hover .gm-sb-tz-icon,
  .gm-sb-tz.v-popper--shown .gm-sb-tz-icon {
    color: var(--color-neutral-900);
  }
  .gm-sb-tz-body {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}
</style>
